import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  BillingDashboardParams,
  BillingListParams,
  billingService,
  BillingVerificationPatch,
  RecordInvoicePayload,
  SendInternalNoticePayload,
  SendInvoicePayload,
} from '@/services/billing.service';
import { QUERY_KEYS } from '@/utils/constants';

export const useBillingClients = (params: BillingListParams) =>
  useQuery({
    queryKey: [...QUERY_KEYS.BILLING.LIST, params],
    queryFn: () => billingService.list(params),
    placeholderData: keepPreviousData,
  });

export const useBillingMetrics = () =>
  useQuery({
    queryKey: QUERY_KEYS.BILLING.METRICS,
    queryFn: () => billingService.metrics(),
  });

export const useBillingDashboard = (params: BillingDashboardParams) =>
  useQuery({
    queryKey: [...QUERY_KEYS.BILLING.DASHBOARD, params],
    queryFn: () => billingService.dashboard(params),
    placeholderData: keepPreviousData,
  });

// Every billing hook takes the ORDER being worked as well as the lead, because
// the queue is one row per order (ADR-048): without it the server picks an
// order itself and a client with two open orders would be worked on the wrong
// one. It is part of the cache key for the same reason.
export const useBillingClient = (id: number, orderId?: number, enabled = true) =>
  useQuery({
    queryKey: [...QUERY_KEYS.BILLING.BY_ID(id), orderId ?? null],
    queryFn: () => billingService.getById(id, orderId),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });

// Invalidates every billing query so the queue, dashboard widgets and the open
// detail all reconcile after a workflow transition. onSettled (success AND
// error) mirrors useLeads: a slow send can complete server-side after the client
// aborts, so refetching on error self-heals the UI.
export const useInvalidateBilling = () => {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.BILLING.ROOT });
  };
};

export const useClaimBilling = (id: number, orderId?: number) => {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: () => billingService.claim(id, orderId),
    onSettled: invalidate,
  });
};

export const useReleaseBilling = (id: number, orderId?: number) => {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: () => billingService.release(id, orderId),
    onSettled: invalidate,
  });
};

export const useUpdateVerificationData = (id: number, orderId?: number) => {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: (patch: BillingVerificationPatch) =>
      billingService.updateVerificationData(id, patch, orderId),
    onSettled: invalidate,
  });
};

export const useSendBillingCorrection = (id: number, orderId?: number) => {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: (payload: SendInternalNoticePayload) =>
      billingService.sendCorrection(id, payload, orderId),
    onSettled: invalidate,
  });
};

export const useVerifyBilling = (id: number, orderId?: number) => {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: (notes?: string) => billingService.verify(id, notes, orderId),
    onSettled: invalidate,
  });
};

export const useRecordInvoice = (id: number, orderId?: number) => {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: (payload: RecordInvoicePayload) =>
      billingService.recordInvoice(id, payload, orderId),
    onSettled: invalidate,
  });
};

export const useNotifyService = (id: number, orderId?: number) => {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: (payload: SendInternalNoticePayload) =>
      billingService.notifyService(id, payload, orderId),
    onSettled: invalidate,
  });
};

export const useSendClientInvoice = (id: number, orderId?: number) => {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: (payload: SendInvoicePayload) => billingService.sendInvoice(id, payload, orderId),
    onSettled: invalidate,
  });
};

export const useCompleteBilling = (id: number, orderId?: number) => {
  const invalidate = useInvalidateBilling();
  return useMutation({
    mutationFn: () => billingService.complete(id, orderId),
    onSettled: invalidate,
  });
};
